import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  BarChart3, 
  Package, 
  CheckCircle, 
  AlertTriangle, 
  Clock,
  QrCode,
  Search
} from 'lucide-react';
import { useAuth } from '../contexts';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuthCheck } from '../hooks/useAuthCheck';
import { apiService } from '../services/api';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;

const Header = styled.header`
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const HeaderTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography['2xl']};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
`;

const HeaderSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    justify-content: space-between;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
  }
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.gray100};
  color: ${({ theme }) => theme.colors.textSecondary};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.gray200};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const Main = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing[6]};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing[6]};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const ProminentStatCard = styled(StatCard)`
  background: linear-gradient(135deg, #3b82f6 60%, #60a5fa 100%);
  color: #fff;
  box-shadow: 0 4px 32px 0 rgba(59,130,246,0.15);
  border: none;
  transform: scale(1.08);
  transition: box-shadow 0.3s, transform 0.2s;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:hover {
    box-shadow: 0 8px 40px 0 rgba(59,130,246,0.25);
    transform: scale(1.12);
  }
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography['3xl']};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const ProminentStatValue = styled(StatValue)`
  font-size: 3.5rem;
  font-weight: 800;
  color: #fff;
  text-shadow: 0 2px 8px rgba(59,130,246,0.15);
`;

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: ${({ theme }) => theme.typography.medium};
`;

const ProminentStatLabel = styled(StatLabel)`
  color: #e0e7ef;
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 0.5rem;
`;

const StatChange = styled.div<{ positive?: boolean }>`
  font-size: ${({ theme }) => theme.typography.xs};
  color: ${({ theme, positive }) => positive ? theme.colors.success : theme.colors.error};
  font-weight: ${({ theme }) => theme.typography.medium};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.xl};
  font-weight: ${({ theme }) => theme.typography.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const QuickActionCard = styled.button`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing[6]};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const QuickActionIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: ${({ theme }) => theme.colors.primaryBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
`;

const QuickActionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.base};
  font-weight: ${({ theme }) => theme.typography.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const QuickActionDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProjects: 0
  });

  useAuthCheck();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const projects = await apiService.getProjects();
        setStats({
          totalProjects: projects.length
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'scan':
        navigate('/scanner');
        break;
      case 'cartons':
        navigate('/cartons');
        break;
      case 'projects':
        navigate('/projects');
        break;
      case 'issues':
        navigate('/cartons');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <DashboardContainer>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}>
          <LoadingSpinner size="large" text="Loading dashboard..." />
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Header>
        <HeaderContent>
          <HeaderLeft>
            <HeaderTitle>Welcome back, {user?.username}!</HeaderTitle>
            <HeaderSubtitle>Here's what's happening with your projects today.</HeaderSubtitle>
          </HeaderLeft>
          <HeaderActions>
            {/*<SearchButton>
              <Search size={20} />
            </SearchButton>*/}
            {/*<ActionButton>
              <QrCode size={20} />
              Scan QR
            </ActionButton>*/}
          </HeaderActions>
        </HeaderContent>
      </Header>

      <Main>
        <StatsGrid>
          <ProminentStatCard>
            <StatHeader>
              <div style={{ width: '100%' }}>
                <ProminentStatValue>{stats.totalProjects}</ProminentStatValue>
                <ProminentStatLabel>Total Projects</ProminentStatLabel>
              </div>
              {/*<StatIcon color="#fff">
                <BarChart3 size={36} />
              </StatIcon>*/}
            </StatHeader>
          </ProminentStatCard>
        </StatsGrid>

        <SectionTitle>Quick Actions</SectionTitle>
        <QuickActions>
          {/*<QuickActionCard onClick={() => handleQuickAction('scan')}>
            <QuickActionIcon>
              <QrCode size={32} />
            </QuickActionIcon>
            <QuickActionTitle>Scan Carton</QuickActionTitle>
            <QuickActionDescription>
              Quickly scan a carton QR code to view details or mark as received
            </QuickActionDescription>
          </QuickActionCard>*/}
          
          <QuickActionCard onClick={() => handleQuickAction('projects')}>
            <QuickActionIcon>
              <BarChart3 size={32} />
            </QuickActionIcon>
            <QuickActionTitle>Projects</QuickActionTitle>
            <QuickActionDescription>
              Monitor project progress and view detailed analytics
            </QuickActionDescription>
          </QuickActionCard>

          <QuickActionCard onClick={() => handleQuickAction('cartons')}>
            <QuickActionIcon>
              <Package size={32} />
            </QuickActionIcon>
            <QuickActionTitle>Cartons</QuickActionTitle>
            <QuickActionDescription>
              Browse all cartons, filter by status, and manage inventory
            </QuickActionDescription>
          </QuickActionCard>

          

          {/*<QuickActionCard onClick={() => handleQuickAction('issues')}>
            <QuickActionIcon>
              <AlertTriangle size={32} />
            </QuickActionIcon>
            <QuickActionTitle>Issues</QuickActionTitle>
            <QuickActionDescription>
              View and manage reported issues
            </QuickActionDescription>
          </QuickActionCard>*/}
        </QuickActions>
      </Main>
    </DashboardContainer>
  );
};

export default DashboardPage; 